import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  Avatar,
  Confirm,
  DataTable,
  ErrorState,
  Field,
  Modal,
  Pagination,
  Spinner,
  StatusBadge
} from '../../components/ui.jsx';
import { api, toQuery } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { homeFor, useAuth } from '../../state/AuthContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { formatDate, titleCase } from '../../lib/format.js';

const KycModal = ({ user, onClose, onDone }) => {
  const toast = useToast();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const decide = async (kycStatus) => {
    if (kycStatus === 'rejected' && !note.trim()) {
      toast.error('Say why so the landlord can send a better copy.');
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { kycStatus, kycNote: note.trim() || null });
      toast.success(kycStatus === 'verified' ? 'Landlord verified.' : 'Verification rejected.');
      onDone();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Verify ${user.name}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={() => decide('rejected')} disabled={busy}>
            Reject
          </button>
          <button type="button" className="btn btn-primary" onClick={() => decide('verified')} disabled={busy}>
            Verify
          </button>
        </>
      }
    >
      <dl className="kv-block">
        <div>
          <dt>Name</dt>
          <dd>{user.name}</dd>
        </div>
        <div>
          <dt>Business</dt>
          <dd>{user.businessName || '-'}</dd>
        </div>
        <div>
          <dt>Email</dt>
          <dd>{user.email}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{user.phone || '-'}</dd>
        </div>
        <div>
          <dt>Listings</dt>
          <dd>{user.listingCount}</dd>
        </div>
        <div>
          <dt>Current state</dt>
          <dd>
            <StatusBadge status={user.kycStatus} />
          </dd>
        </div>
      </dl>

      {user.kycDocumentUrl ? (
        <p>
          <a className="btn btn-outline btn-sm" href={user.kycDocumentUrl} target="_blank" rel="noreferrer noopener">
            Open the uploaded document
          </a>
        </p>
      ) : (
        <p className="notice notice-warn">This landlord has not uploaded anything yet.</p>
      )}

      <Field label="Note" hint="Shown to the landlord if you reject.">
        <textarea rows="3" value={note} onChange={(event) => setNote(event.target.value)} />
      </Field>
    </Modal>
  );
};

const AdminUsers = () => {
  useTitle('User management');
  const { user: me, assumeSession } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const role = params.get('role') || 'any';
  const kyc = params.get('kyc') || 'any';
  const active = params.get('active') || 'any';
  const q = params.get('q') || '';
  const page = Number(params.get('page') || 1);

  const [search, setSearch] = useState(q);
  const [kycTarget, setKycTarget] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [issuedPassword, setIssuedPassword] = useState('');
  const [resetBusy, setResetBusy] = useState(false);

  const { data, loading, error, reload } = useFetch(
    `/admin/users${toQuery({ role, kyc, active, q, page, pageSize: 15 })}`
  );

  const setParam = (patch) =>
    setParams(new URLSearchParams(toQuery({ role, kyc, active, q, page: 1, ...patch }).replace(/^\?/, '')));

  const patchUser = async (user, body, message) => {
    try {
      await api.patch(`/admin/users/${user.id}`, body);
      toast.success(message);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/admin/users/${deleting.id}`);
      toast.success('Account deleted.');
      setDeleting(null);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const openEmailEditor = (user) => {
    setEmailTarget(user);
    setEmailDraft(user.email || '');
  };

  const saveEmail = async () => {
    if (!emailTarget) return;
    setSavingEmail(true);
    try {
      await api.patch(`/admin/users/${emailTarget.id}`, { email: emailDraft.trim() });
      toast.success('Email updated.');
      setEmailTarget(null);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setSavingEmail(false);
    }
  };

  const openResetPassword = (user) => {
    setResetTarget(user);
    setResetPassword('');
    setIssuedPassword('');
  };

  const submitResetPassword = async () => {
    if (!resetTarget) return;
    setResetBusy(true);
    try {
      const payload = { mustChangePassword: true, activate: true };
      if (resetPassword.trim()) payload.password = resetPassword.trim();
      const data = await api.post(`/admin/users/${resetTarget.id}/reset-password`, payload);
      setIssuedPassword(data.temporaryPassword || '');
      toast.success('Password reset complete.');
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setResetBusy(false);
    }
  };

  const accessAccount = async (target) => {
    try {
      const data = await api.post(`/admin/users/${target.id}/impersonate`, {});
      await assumeSession(data.token, data.user);
      toast.success(`Signed in as ${target.name}.`);
      navigate(homeFor(data.user), { replace: true });
    } catch (requestError) {
      toast.error(requestError.message);
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'User',
      render: (user) => (
        <div className="cell-listing">
          <Avatar name={user.name} url={user.avatarUrl} size={40} />
          <div>
            <strong>{user.name}</strong>
            {user.businessName ? <span className="muted">{user.businessName}</span> : null}
            <span className="muted">{user.email}</span>
            <span className="muted">{user.phone}</span>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      header: 'Role',
      render: (user) => (
        <select
          value={user.role}
          onChange={(event) => patchUser(user, { role: event.target.value }, `${user.name} is now a ${event.target.value}.`)}
          disabled={user.id === me.id}
          aria-label={`Role for ${user.name}`}
        >
          {['tenant', 'landlord', 'admin'].map((value) => (
            <option key={value} value={value}>
              {titleCase(value)}
            </option>
          ))}
        </select>
      )
    },
    {
      key: 'kyc',
      header: 'Verification',
      render: (user) =>
        user.role === 'landlord' ? (
          <div className="cell-stack">
            <StatusBadge status={user.kycStatus} />
            <button type="button" className="link-btn" onClick={() => setKycTarget(user)}>
              Review
            </button>
          </div>
        ) : (
          <span className="muted">Not needed</span>
        )
    },
    {
      key: 'activity',
      header: 'Activity',
      render: (user) => (
        <div className="cell-stack">
          <span>{user.listingCount} listings</span>
          <span>{user.applicationCount} applications</span>
          <span className="muted">joined {formatDate(user.createdAt)}</span>
        </div>
      )
    },
    {
      key: 'state',
      header: 'Access',
      render: (user) => <StatusBadge status={user.isActive ? 'approved' : 'rejected'} label={user.isActive ? 'Active' : 'Suspended'} />
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (user) => (
        <div className="cell-actions">
          {user.id === me.id ? (
            <span className="muted">This is you</span>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => openEmailEditor(user)}
              >
                Edit email
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => openResetPassword(user)}
              >
                Reset password
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => accessAccount(user)}
              >
                Access account
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() =>
                  patchUser(
                    user,
                    { isActive: !user.isActive },
                    user.isActive ? `${user.name} suspended.` : `${user.name} reinstated.`
                  )
                }
              >
                {user.isActive ? 'Suspend' : 'Reinstate'}
              </button>
              <button type="button" className="link-btn danger-link" onClick={() => setDeleting(user)}>
                Delete
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Users</h1>
          <p className="muted">Roles, landlord verification and account access.</p>
        </div>
      </header>

      <div className="filter-bar">
        <label className="inline-select">
          <span>Role</span>
          <select value={role} onChange={(event) => setParam({ role: event.target.value })}>
            <option value="any">All roles</option>
            <option value="tenant">Tenants</option>
            <option value="landlord">Landlords</option>
            <option value="admin">Administrators</option>
          </select>
        </label>

        <label className="inline-select">
          <span>Verification</span>
          <select value={kyc} onChange={(event) => setParam({ kyc: event.target.value })}>
            <option value="any">Any state</option>
            <option value="pending">Waiting for review</option>
            <option value="verified">Verified</option>
            <option value="rejected">Rejected</option>
            <option value="unsubmitted">Nothing submitted</option>
          </select>
        </label>

        <label className="inline-select">
          <span>Access</span>
          <select value={active} onChange={(event) => setParam({ active: event.target.value })}>
            <option value="any">Any</option>
            <option value="true">Active</option>
            <option value="false">Suspended</option>
          </select>
        </label>

        <form
          className="search-inline"
          onSubmit={(event) => {
            event.preventDefault();
            setParam({ q: search.trim() });
          }}
        >
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Name, email or phone"
          />
          <button type="submit" className="btn btn-outline btn-sm">
            Search
          </button>
        </form>
      </div>

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}

      {data ? (
        <>
          <DataTable columns={columns} rows={data.items} empty={{ title: 'No users match those filters' }} />
          <Pagination page={data.page} totalPages={data.totalPages} onChange={(next) => setParam({ page: next })} />
        </>
      ) : null}

      {kycTarget ? (
        <KycModal
          user={kycTarget}
          onClose={() => setKycTarget(null)}
          onDone={() => {
            setKycTarget(null);
            reload();
          }}
        />
      ) : null}

      {emailTarget ? (
        <Modal
          title={`Update email for ${emailTarget.name}`}
          onClose={() => setEmailTarget(null)}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setEmailTarget(null)} disabled={savingEmail}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={saveEmail} disabled={savingEmail}>
                {savingEmail ? 'Saving...' : 'Save email'}
              </button>
            </>
          }
        >
          <Field label="Email address" required>
            <input
              type="email"
              value={emailDraft}
              onChange={(event) => setEmailDraft(event.target.value)}
              autoComplete="email"
            />
          </Field>
        </Modal>
      ) : null}

      {resetTarget ? (
        <Modal
          title={`Reset password for ${resetTarget.name}`}
          onClose={() => setResetTarget(null)}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setResetTarget(null)} disabled={resetBusy}>
                Close
              </button>
              <button type="button" className="btn btn-primary" onClick={submitResetPassword} disabled={resetBusy}>
                {resetBusy ? 'Resetting...' : 'Reset password'}
              </button>
            </>
          }
        >
          <Field label="New password (optional)">
            <input
              type="text"
              value={resetPassword}
              onChange={(event) => setResetPassword(event.target.value)}
              placeholder="Leave blank to auto-generate a strong temporary password"
            />
          </Field>
          {issuedPassword ? (
            <p className="notice notice-good">Temporary password: {issuedPassword}</p>
          ) : (
            <p className="field-hint">The user will be forced to change this password at first sign-in.</p>
          )}
        </Modal>
      ) : null}

      {deleting ? (
        <Confirm
          title={`Delete ${deleting.name}?`}
          message="Their listings, applications and messages go with them. Suspending is usually the better move, because it is reversible."
          confirmLabel="Delete the account"
          busy={busy}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
};

export default AdminUsers;
